import { ChainId, JSBI, Percent, Token, WETC } from '@etcswapv2/sdk-legacy'

// V2 Router addresses
export const ROUTER_ADDRESS: { [chainId: number]: string } = {
  [ChainId.CLASSIC]: '0x79Bf07555C34e68C4Ae93642d1007D7f908d60F5',
  [ChainId.MORDOR]: '0x6d194227a9A1C11f144B35F96E6289c5602Da493',
}

// Get router address for a chain
export function getRouterAddress(chainId: ChainId | undefined): string | undefined {
  if (!chainId) return undefined
  return ROUTER_ADDRESS[chainId]
}

// a list of tokens by chain
type ChainTokenList = {
  readonly [chainId in ChainId]?: Token[]
}

// Classic USD Stablecoin on ETC mainnet and Mordor
export const USC: { [chainId: number]: Token } = {
  [ChainId.CLASSIC]: new Token(ChainId.CLASSIC, '0xDE093684c796204224BC081f937aa059D903c52a', 6, 'USC', 'Classic USD'),
  [ChainId.MORDOR]: new Token(ChainId.MORDOR, '0xDE093684c796204224BC081f937aa059D903c52a', 6, 'USC', 'Classic USD'),
}

// Helper to safely get WETC or empty array
const getWetcArray = (chainId: ChainId): Token[] => {
  const wetc = WETC[chainId]
  return wetc ? [wetc] : []
}

// Helper to get USC for a chain
const getUscArray = (chainId: ChainId): Token[] => {
  const usc = USC[chainId]
  return usc ? [usc] : []
}

const _WETC_ONLY: ChainTokenList = {
  [ChainId.CLASSIC]: getWetcArray(ChainId.CLASSIC),
  [ChainId.MORDOR]: getWetcArray(ChainId.MORDOR),
}

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  [ChainId.CLASSIC]: [...getWetcArray(ChainId.CLASSIC), ...getUscArray(ChainId.CLASSIC)],
  [ChainId.MORDOR]: [...getWetcArray(ChainId.MORDOR), ...getUscArray(ChainId.MORDOR)],
}

/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 */
export const CUSTOM_BASES: { [chainId in ChainId]?: { [tokenAddress: string]: Token[] } } = {}

// used for display in the default list when adding liquidity
export const SUGGESTED_BASES: ChainTokenList = {
  [ChainId.CLASSIC]: [...getWetcArray(ChainId.CLASSIC), ...getUscArray(ChainId.CLASSIC)],
  [ChainId.MORDOR]: [...getWetcArray(ChainId.MORDOR), ...getUscArray(ChainId.MORDOR)],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  [ChainId.CLASSIC]: [...getWetcArray(ChainId.CLASSIC), ...getUscArray(ChainId.CLASSIC)],
  [ChainId.MORDOR]: [...getWetcArray(ChainId.MORDOR), ...getUscArray(ChainId.MORDOR)],
}

export const PINNED_PAIRS: { readonly [chainId in ChainId]?: [Token, Token][] } = {
  [ChainId.CLASSIC]: [],
  [ChainId.MORDOR]: [],
}

export interface WalletInfo {
  connector?: unknown
  name: string
  iconName: string
  description: string
  href: string | null
  color: string
  primary?: true
  mobile?: true
  mobileOnly?: true
}

// Wallet connections are now handled by RainbowKit
// These are kept for compatibility but not used
export const SUPPORTED_WALLETS: { [key: string]: WalletInfo } = {
  INJECTED: {
    connector: null,
    name: 'Injected',
    iconName: 'arrow-right.svg',
    description: 'Injected web3 provider.',
    href: null,
    color: '#010101',
    primary: true,
  },
  METAMASK: {
    connector: null,
    name: 'MetaMask',
    iconName: 'metamask.png',
    description: 'Easy-to-use browser extension.',
    href: null,
    color: '#E8831D',
  },
}

export const NetworkContextName = 'NETWORK'

// ECNS (Ethereum Classic Name Service) contract addresses
// Mordor testnet deployed 2026-02-06
// ETC mainnet pending deployment
export const ECNS_REGISTRY_ADDRESS: { [chainId: number]: string } = {
  [ChainId.MORDOR]: '0x298195a795a5fe91bb47db1c4e501f07767775c8',
  // [ChainId.CLASSIC]: '0x...', // Pending deployment
}

export const ECNS_PUBLIC_RESOLVER_ADDRESS: { [chainId: number]: string } = {
  [ChainId.MORDOR]: '0xf8ee21672f4077354091aa5b1a147fde25ae81bc',
  // [ChainId.CLASSIC]: '0x...', // Pending deployment
}

export const ECNS_REVERSE_REGISTRAR_ADDRESS: { [chainId: number]: string } = {
  [ChainId.MORDOR]: '0xab9ffcf5ccaaf0f276a7c9813d57a8418e7e9f6a',
  // [ChainId.CLASSIC]: '0x...', // Pending deployment
}

// default allowed slippage, in bips
export const INITIAL_ALLOWED_SLIPPAGE = 50
// 20 minutes, denominated in seconds
export const DEFAULT_DEADLINE_FROM_NOW = 60 * 20

// one basis point
export const ONE_BIPS = new Percent(JSBI.BigInt(1), JSBI.BigInt(10000))
export const BIPS_BASE = JSBI.BigInt(10000)
// used for warning states
export const ALLOWED_PRICE_IMPACT_LOW: Percent = new Percent(JSBI.BigInt(100), BIPS_BASE) // 1%
export const ALLOWED_PRICE_IMPACT_MEDIUM: Percent = new Percent(JSBI.BigInt(300), BIPS_BASE) // 3%
export const ALLOWED_PRICE_IMPACT_HIGH: Percent = new Percent(JSBI.BigInt(500), BIPS_BASE) // 5%
// if the price slippage exceeds this number, force the user to type 'confirm' to execute
export const PRICE_IMPACT_WITHOUT_FEE_CONFIRM_MIN: Percent = new Percent(JSBI.BigInt(1000), BIPS_BASE) // 10%
// for non expert mode disable swaps above this
export const BLOCKED_PRICE_IMPACT_NON_EXPERT: Percent = new Percent(JSBI.BigInt(1500), BIPS_BASE) // 15%

// used to ensure the user doesn't send so much ETC so they end up with <.01
export const MIN_ETH = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(16)) // .01 ETC
export const BETTER_TRADE_LINK_THRESHOLD = new Percent(JSBI.BigInt(75), JSBI.BigInt(10000))
